import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  promptList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 260,
    overflowY: "auto",
    padding: theme.spacing(0.5, 1),
  },
  emptyPrompts: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  promptRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const parseConfig = (jsonContent) => {
  if (!jsonContent) {
    return { promptIds: [], defaultPromptId: null };
  }

  try {
    const parsed = JSON.parse(jsonContent);
    const promptIds = Array.isArray(parsed.promptIds)
      ? parsed.promptIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];
    return {
      promptIds,
      defaultPromptId: parsed.defaultPromptId ? Number(parsed.defaultPromptId) : null,
    };
  } catch {
    return { promptIds: [], defaultPromptId: null };
  }
};

const ChatAgentIntegrationModal = ({ open, onClose, integrationId }) => {
  const classes = useStyles();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);

  const initialState = useMemo(
    () => ({
      name: "",
      promptIds: [],
      defaultPromptId: null,
    }),
    []
  );

  const [formValues, setFormValues] = useState(initialState);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;

    const loadData = async () => {
      setLoading(true);
      try {
        const promptsRequest = api.get("/prompt");
        const integrationRequest = integrationId
          ? api.get(`/queueIntegration/${integrationId}`)
          : Promise.resolve(null);

        const [promptsResponse, integrationResponse] = await Promise.all([
          promptsRequest,
          integrationRequest,
        ]);

        if (!active) return;

        setPrompts(promptsResponse.data?.prompts || []);

        if (integrationResponse?.data) {
          const integration = integrationResponse.data;
          const config = parseConfig(integration.jsonContent);
          setFormValues({
            name: integration.name || "",
            promptIds: config.promptIds,
            defaultPromptId: config.defaultPromptId || config.promptIds[0] || null,
          });
        } else {
          setFormValues(initialState);
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [open, integrationId, initialState]);

  const handleClose = () => {
    setFormValues(initialState);
    onClose();
  };

  const schema = Yup.object().shape({
    name: Yup.string().min(2).max(100).required(i18n.t("common.required")),
    promptIds: Yup.array().of(Yup.number()).min(1, i18n.t("chatAgentIntegrationModal.form.promptsRequired")),
    defaultPromptId: Yup.number().nullable(),
  });

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const defaultPromptId =
        values.defaultPromptId && values.promptIds.includes(Number(values.defaultPromptId))
          ? Number(values.defaultPromptId)
          : values.promptIds[0] || null;

      const payload = {
        type: "chat_agent",
        name: values.name,
        projectName: values.name,
        language: "es",
        jsonContent: JSON.stringify({
          promptIds: values.promptIds,
          defaultPromptId,
          rules: { manualOnly: true },
        }),
      };

      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, payload);
        toast.success(i18n.t("chatAgentIntegrationModal.success.edit"));
      } else {
        await api.post("/queueIntegration", payload);
        toast.success(i18n.t("chatAgentIntegrationModal.success.add"));
      }

      handleClose();
    } catch (err) {
      toastError(err);
      setSubmitting(false);
    }
  };

  const togglePromptId = (promptId, currentIds, setFieldValue, values) => {
    const normalizedId = Number(promptId);
    const nextIds = currentIds.includes(normalizedId)
      ? currentIds.filter((id) => id !== normalizedId)
      : [...currentIds, normalizedId];

    let nextDefault = values.defaultPromptId;
    if (!nextIds.includes(Number(nextDefault))) {
      nextDefault = nextIds[0] || null;
    }

    setFieldValue("promptIds", nextIds);
    setFieldValue("defaultPromptId", nextDefault);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {integrationId
          ? i18n.t("chatAgentIntegrationModal.title.edit")
          : i18n.t("chatAgentIntegrationModal.title.add")}
      </DialogTitle>

      {loading ? (
        <DialogContent dividers>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        </DialogContent>
      ) : (
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={handleSave}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("chatAgentIntegrationModal.form.name")}
                  name="name"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                <Box mt={2} mb={1}>
                  <Typography variant="subtitle2">
                    {i18n.t("chatAgentIntegrationModal.form.promptsTitle")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("chatAgentIntegrationModal.form.promptsHint")}
                  </Typography>
                </Box>

                <Box className={classes.promptList}>
                  {!prompts.length ? (
                    <Typography className={classes.emptyPrompts}>
                      {i18n.t("chatAgentIntegrationModal.form.noPrompts")}
                    </Typography>
                  ) : (
                    prompts.map((prompt) => (
                      <Box key={prompt.id} className={classes.promptRow}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={values.promptIds.includes(Number(prompt.id))}
                              onChange={() =>
                                togglePromptId(prompt.id, values.promptIds, setFieldValue, values)
                              }
                            />
                          }
                          label={`${prompt.name} (${prompt.model || "Open AI"})`}
                        />
                        {values.promptIds.includes(Number(prompt.id)) && (
                          <Radio
                            color="primary"
                            size="small"
                            checked={Number(values.defaultPromptId) === Number(prompt.id)}
                            onChange={() => setFieldValue("defaultPromptId", Number(prompt.id))}
                          />
                        )}
                      </Box>
                    ))
                  )}
                </Box>

                {values.promptIds.length > 0 && (
                  <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                    {i18n.t("chatAgentIntegrationModal.form.defaultHint")}
                  </Typography>
                )}

                {touched.promptIds && errors.promptIds && (
                  <Typography variant="caption" color="error" display="block" mt={1}>
                    {errors.promptIds}
                  </Typography>
                )}
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} color="secondary" variant="outlined" disabled={isSubmitting}>
                  {i18n.t("chatAgentIntegrationModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                  className={classes.btnWrapper}
                >
                  {integrationId
                    ? i18n.t("chatAgentIntegrationModal.buttons.save")
                    : i18n.t("chatAgentIntegrationModal.buttons.add")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default ChatAgentIntegrationModal;
